import type { AuditLogPage } from '../../api/audit-log.service';
import type {
  TenantDetail,
  TenantSettings,
  UpdateTenantPaymentsSettingsDto,
  UpdateTenantSettingsRequest,
} from '../../api/tenant.service';
import { route, type HandlerEntry } from '../handler-registry';

let DEMO_SETTINGS: TenantSettings = {
  countryCode: 'RO',
  locale: 'ro-RO',
  timezone: 'Europe/Bucharest',
  currency: 'RON',
  branding: {
    logoUrl: null,
    coverPhotoUrl: null,
    primaryColorHex: '#2E6BFF',
    accentColorHex: '#2E6BFF',
    hideSportDeskMentions: false,
  },
  features: {
    competitionsEnabled: true,
    customXpRulesEnabled: true,
    announcementsEnabled: true,
    parentAccountsEnabled: false,
    qrCheckInEnabled: false,
    leaderboardEnabled: true,
    publicMiniSiteEnabled: false,
    contractSigningEnabled: false,
  },
  terminologyByLocale: {},
  memberTitle: null,
  payments: {
    enabled: true,
    dueDay: 10,
    defaultMethod: 'Cash',
    maxUploadBytes: 5 * 1024 * 1024,
    allowedContentTypes: ['image/jpeg', 'image/png', 'application/pdf'],
    bankReconciliationEnabled: false,
  },
  xp: {
    levelBaseXp: 100,
    maxLevel: 99,
  },
};

const EMPTY_AUDIT_PAGE: AuditLogPage = { items: [], nextCursor: null };

export const catchallHandlers: readonly HandlerEntry[] = [
  route('GET', /^\/api\/tenants\/([^/]+)$/, (_ctx, data): TenantDetail => {
    const now = new Date().toISOString();
    return {
      tenantId: data.tenant.tenantId,
      subdomain: data.tenant.subdomain,
      teamName: data.tenant.public.teamName,
      discipline: data.tenant.public.discipline,
      status: 'Active',
      plan: 'Pro',
      createdAt: now,
      trialStartedAt: now,
      trialEndsAt: now,
      planChangedAt: null,
      loginUrl: data.tenant.public.loginUrl,
      role: 'Owner',
      settings: {
        ...DEMO_SETTINGS,
        terminologyByLocale: data.tenant.public.terminologyByLocale,
      },
      company: null,
    };
  }),

  route('GET', /^\/api\/tenants\/([^/]+)\/settings$/, (_ctx, data) => ({
    ...DEMO_SETTINGS,
    terminologyByLocale: data.tenant.public.terminologyByLocale,
  })),
  route('PATCH', /^\/api\/tenants\/([^/]+)\/settings$/, (ctx, data) => {
    const body = (ctx.body ?? {}) as UpdateTenantSettingsRequest;
    DEMO_SETTINGS = {
      ...DEMO_SETTINGS,
      locale: body.locale ?? DEMO_SETTINGS.locale,
      timezone: body.timezone ?? DEMO_SETTINGS.timezone,
    };
    return {
      ...DEMO_SETTINGS,
      terminologyByLocale: data.tenant.public.terminologyByLocale,
    };
  }),
  route('PATCH', /^\/api\/tenants\/([^/]+)\/settings\/payments$/, (ctx, data) => {
    const body = (ctx.body ?? {}) as UpdateTenantPaymentsSettingsDto;
    DEMO_SETTINGS = {
      ...DEMO_SETTINGS,
      payments: {
        ...DEMO_SETTINGS.payments,
        enabled: body.enabled ?? DEMO_SETTINGS.payments.enabled,
        dueDay: body.dueDay ?? DEMO_SETTINGS.payments.dueDay,
        defaultMethod: body.defaultMethod ?? DEMO_SETTINGS.payments.defaultMethod,
        bankReconciliationEnabled:
          body.bankReconciliationEnabled ??
          DEMO_SETTINGS.payments.bankReconciliationEnabled,
      },
    };
    return {
      ...DEMO_SETTINGS,
      terminologyByLocale: data.tenant.public.terminologyByLocale,
    };
  }),
  route('GET', /^\/api\/tenants\/([^/]+)\/settings\/member-title$/, () => null),
  route('PUT', /^\/api\/tenants\/([^/]+)\/settings\/member-title$/, () => null),
  route('DELETE', /^\/api\/tenants\/([^/]+)\/settings\/member-title$/, () => null),
  route('PUT', /^\/api\/tenants\/([^/]+)\/athletes\/([^/]+)\/title$/, () => null),
  route('DELETE', /^\/api\/tenants\/([^/]+)\/athletes\/([^/]+)\/title$/, () => null),

  route('GET', /^\/api\/tenants\/([^/]+)\/events$/, () => []),
  route('GET', /^\/api\/tenants\/([^/]+)\/events\/([^/]+)$/, () => ({
    error: { status: 404, message: 'Not found' },
  })),

  route('GET', /^\/api\/tenants\/([^/]+)\/audit-log$/, () => EMPTY_AUDIT_PAGE),

  route('GET', /^\/api\/tenants\/([^/]+)\/xp\/rules$/, () => []),
  route('PUT', /^\/api\/tenants\/([^/]+)\/xp\/rules\/([^/]+)$/, () => null),
  route('POST', /^\/api\/tenants\/([^/]+)\/xp\/rules\/reset$/, () => null),
  route('GET', /^\/api\/tenants\/([^/]+)\/xp\/athletes\/([^/]+)\/state$/, (ctx) => ({
    athleteTenantAccountId: decodeURIComponent(ctx.match[2]),
    totalXp: 0,
    level: 1,
    xpIntoLevel: 0,
    xpForNextLevel: 100,
    lastTransactionAt: null,
  })),
  route('GET', /^\/api\/tenants\/([^/]+)\/xp\/athletes\/([^/]+)\/transactions$/, () => []),
  route('GET', /^\/api\/tenants\/([^/]+)\/xp\/me\/state$/, (_ctx, data) => ({
    athleteTenantAccountId: data.currentMemberId,
    totalXp: 0,
    level: 1,
    xpIntoLevel: 0,
    xpForNextLevel: 100,
    lastTransactionAt: null,
  })),
  route('GET', /^\/api\/tenants\/([^/]+)\/xp\/me\/transactions$/, () => []),
  route('GET', /^\/api\/tenants\/([^/]+)\/xp\/leaderboard$/, () => []),

  route('GET', /^\/api\/tenants\/([^/]+)\/payments\/([^/]+)\/proofs$/, () => []),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/payments\/([^/]+)\/proofs\/presign$/,
    () => ({ uploadUrl: '#', proofId: 'demo-proof', expiresAt: new Date().toISOString() }),
  ),
  route(
    'DELETE',
    /^\/api\/tenants\/([^/]+)\/payments\/([^/]+)\/proofs\/([^/]+)$/,
    () => null,
  ),

  route('GET', /^\/api\/tenants\/([^/]+)\/class-schedules\/([^/]+)\/exceptions$/, () => []),

  route('GET', /^\/api\/me\/tenants$/, (_ctx, data) => [
    {
      tenantId: data.tenant.tenantId,
      subdomain: data.tenant.subdomain,
      teamName: data.tenant.public.teamName,
      logoUrl: null,
      loginUrl: data.tenant.public.loginUrl,
      plan: 'Pro' as const,
      status: 'Active' as const,
    },
  ]),

  route('GET', /^\/api\/tenants-public$/, (_ctx, data) => [data.tenant.public]),

  route('POST', /^\/api\/tenants\/([^/]+)\/branding\/logo$/, (_ctx, data) => ({
    ...DEMO_SETTINGS,
    terminologyByLocale: data.tenant.public.terminologyByLocale,
  })),
  route('DELETE', /^\/api\/tenants\/([^/]+)\/branding\/logo$/, (_ctx, data) => ({
    ...DEMO_SETTINGS,
    terminologyByLocale: data.tenant.public.terminologyByLocale,
  })),
  route('POST', /^\/api\/tenants\/([^/]+)\/branding\/cover-photo$/, (_ctx, data) => ({
    ...DEMO_SETTINGS,
    terminologyByLocale: data.tenant.public.terminologyByLocale,
  })),
  route('DELETE', /^\/api\/tenants\/([^/]+)\/branding\/cover-photo$/, (_ctx, data) => ({
    ...DEMO_SETTINGS,
    terminologyByLocale: data.tenant.public.terminologyByLocale,
  })),

  route('POST', /^\/api\/tenants\/([^/]+)\/accounts\/([^/]+)\/profile-picture$/, () => ({
    profilePictureUrl: null,
  })),
  route('DELETE', /^\/api\/tenants\/([^/]+)\/accounts\/([^/]+)\/profile-picture$/, () => ({
    profilePictureUrl: null,
  })),

  route('GET', /^\/api\/invitations\/([^/]+)$/, () => ({
    error: { status: 404, message: 'Not found' },
  })),
  route('POST', /^\/api\/invitations\/([^/]+)\/accept$/, () => ({
    error: { status: 404, message: 'Not found' },
  })),
  route('GET', /^\/api\/claim\/([^/]+)$/, () => ({
    error: { status: 404, message: 'Not found' },
  })),
  route('POST', /^\/api\/claim\/([^/]+)$/, () => ({
    error: { status: 404, message: 'Not found' },
  })),
];
